import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useLeadsSDK from '../../hooks/useLeadsSDK';
import { STATES } from '../../data/states.data';

const EnterManualAddressModal = ({ onClose, isBusiness, setAddress }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const leads = useLeadsSDK();
  const { street, apt, city, state, zip } = formData;
  const streetInputRef = useRef(null);

  useEffect(() => {
    if (streetInputRef.current) {
      streetInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    // Validate required fields
    const isValid = street?.trim() && city?.trim() && state?.trim() && zip?.trim();
    setIsFormValid(Boolean(isValid));
  }, [formData, isBusiness]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => {
    const fullAddress = [
      street,
      apt?.trim() ? apt : null,
      city,
      state,
      zip,
    ]
      .filter(Boolean)
      .join(', ');

    leads?.initLeadFromAddress?.(street, apt || '', city, state, zip);

    const addressObject = {
      address1: street,
      address2: apt,
      city,
      state,
      zip
    };

    if (setAddress) {
      // the setAddress function on ServiceabilityCheck sets searchAddress and validation ref
      setAddress(fullAddress, addressObject);
      onClose();
    }
  };

  return createPortal(
    <div className="manual-address-overlay">
      <div className="manual-address-modal">
        <div className="close-btn-wrapper">
          <button onClick={onClose} className="close-btn-red">
            <i className="fas fa-times-circle" style={{ color: '#be2533', fontSize: '24px' }}></i>
          </button>
        </div>

        <div className="modal-orb-wrapper">
          <span className="orb-fallback">
            <i className="fas fa-globe"></i>
          </span>
        </div>

        <h2 className="modal-title">
          {t('manualAddress.modalTitle')}
        </h2>

        <div className="form-grid top-row">
          <input
            ref={streetInputRef}
            type="text"
            name="street"
            placeholder={t('manualAddress.streetAddress')}
            value={formData?.street}
            onChange={handleChange}
            className="form-input"
          />
          <input
            type="text"
            name="apt"
            placeholder={t('manualAddress.aptSteFlrUnit')}
            value={formData?.apt}
            onChange={handleChange}
            className="form-input"
          />
        </div>

        <div className="form-grid bottom-row">
          <input
            type="text"
            name="city"
            placeholder={t('manualAddress.city')}
            value={formData?.city}
            onChange={handleChange}
            className="form-input"
          />
          <select
            name="state"
            value={formData?.state}
            onChange={handleChange}
            className="form-select state-select"
          >
            <option value="" disabled>
              {t('manualAddress.state')}
            </option>
            {Object.keys(STATES).map((abbreviation) => (
              <option key={abbreviation} value={abbreviation}>
                {abbreviation}
              </option>
            ))}
          </select>
          <input
            type="text"
            name="zip"
            placeholder={t('manualAddress.zipCode')}
            value={formData?.zip}
            onChange={handleChange}
            className="form-input"
          />
        </div>

        <div className="submit-btn-wrapper">
          <button
            disabled={!isFormValid}
            className={`submit-btn ${isFormValid ? 'valid' : 'invalid'}`}
            onClick={onSubmit}
            type="button"
          >
            <span>
              <i className="fas fa-chevron-circle-right icon-white" />
            </span>
            <span className="btn-text">{t('manualAddress.checkAvailability')}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EnterManualAddressModal;
